import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";

import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { CreateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { aiSearchResponseService } from "../services/aiSearchResponseService";
import {
  AiSearchResponseValuesInit,
  getAiSearchResponseCreateVariables,
  getAiSearchResponseValues,
  AiSearchResponseValuesType,
  AiSearchResponseErrorsType,
  aiSearchResponseDisplayId,
} from "./AiSearchResponseCommonStore";
import { AiSearchResponse, SortOrder } from "../../../api/graphql";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getAiSearchConversationValues,
  AiSearchConversationValuesType,
} from "../../aiSearchConversation/store/AiSearchConversationCommonStore";
import {
  getUserValues,
  UserValuesType,
} from "../../user/store/UserCommonStore";
import { GridColumn } from "../../common/types";
import { AiSearchToolCallCreateStore } from "../../aiSearchToolCall/store";
import { GridChangeSet } from "../../common/types";
import { EvaluationRating } from "../../../api/graphql";
import { restoredStatus } from "../../common/types";
import { updatedStatus, deleteStatus } from "../../common/types";
import { isOriginalCrudStatus } from "../../common/types";
import { aiSearchConversationService } from "../../aiSearchConversation/services/aiSearchConversationService";
import { userService } from "../../user/services/userService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class AiSearchResponseCreateStore implements CreateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: AiSearchResponseErrorsType = {};
  service = aiSearchResponseService;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda: la capa del formulario (SavingOverlay) lo bloquea.
  isSaving: boolean = false;
  savedEntity: AiSearchResponse | null = null;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = AiSearchResponseValuesInit();
  conversationError: string | undefined;
  conversationService = aiSearchConversationService;
  conversationSearchValue: string = "";
  conversationOptions: AiSearchConversationValuesType[] = [];
  queryError: string | undefined;
  responseError: string | undefined;
  sourcesError: string | undefined;
  explanationError: string | undefined;
  tokenUsageError: string | undefined;
  ratingError: string | undefined;
  evaluatedAtError: string | undefined;
  evaluatedByError: string | undefined;
  evaluatedByService = userService;
  evaluatedBySearchValue: string = "";
  evaluatedByOptions: UserValuesType[] = [];
  toolCallsError: string | undefined;
  toolCallsVisibleColumns: GridColumn[];
  toolCallsCreateStore: AiSearchToolCallCreateStore;
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<AiSearchResponseValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction: (() => void) | null = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: {
        resultId?: string;
        cancelled?: boolean;
        entity?: any;
      }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();
    this.toolCallsVisibleColumns = [
      { name: "sequence", title: this.langStore.AiSearchToolCallSequenceLabel },
      { name: "toolName", title: this.langStore.AiSearchToolCallToolNameLabel },
      { name: "pattern", title: this.langStore.AiSearchToolCallPatternLabel },
      {
        name: "durationMs",
        title: this.langStore.AiSearchToolCallDurationMsLabel,
      },
      { name: "success", title: this.langStore.AiSearchToolCallSuccessLabel },
    ];
    this.toolCallsCreateStore = new AiSearchToolCallCreateStore(
      rootStore,
      "toolCalls_" + this.storeId
    );
    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<AiSearchResponseValuesType>
        );
      }
    });
    makeAutoObservable(this);
    this.setupChangeDetection();
    this.saveOriginalState();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(
    validator: CustomValidator<AiSearchResponseValuesType>
  ) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  get displayId(): string {
    if (this.entityValues) {
      return aiSearchResponseDisplayId(
        this.entityValues as any,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.AiSearchResponseSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  *returnedData(params: CallbackDataParams): any {
    if (
      params.returnData &&
      !params.returnData.cancelled &&
      (params.returnData.entity || params.returnData.resultId)
    ) {
      // Delegate to subentity CreateStore if callerData.entityName matches a subentity
      if (params.callerData?.entityName === "AiSearchToolCall") {
        this.toolCallsCreateStore.isWaitingForCallback = false;
        this.toolCallsCreateStore.callbackReturnPath = null;
        yield this.toolCallsCreateStore.returnedData(params);
        return;
      }

      if (params.callerData?.fieldName === "conversation") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showConversation = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.aiSearchConversationOne({
            id: params.returnData.resultId,
          });
          const aiSearchConversation = result.aiSearchConversationOne;
          this.entityValues.showConversation = {
            displayId: aiSearchConversation.displayId,
            id: aiSearchConversation.id,
          };
          this.setConversationOptions([
            getAiSearchConversationValues(aiSearchConversation, this.langStore),
          ]);
        }
      } else if (params.callerData?.fieldName === "evaluatedBy") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showEvaluatedBy = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.userOne({ id: params.returnData.resultId });
          const user = result.userOne;
          this.entityValues.showEvaluatedBy = {
            displayId: user.displayId,
            id: user.id,
          };
          this.setEvaluatedByOptions([getUserValues(user, this.langStore)]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupAiSearchResponseLabel,
        errors: this.getCardErrors("null"),
      },
    ];
  }

  setConversationOptions(values: AiSearchConversationValuesType[]) {
    this.conversationOptions = values;
  }

  *loadConversationValues(): any {
    let where: any = null;
    if (this.conversationSearchValue) {
      const searchFilter = null;
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.aiSearchConversationListWithOffset({
      offset: 0,
      take: 100,
      where: where,
      orderBy: [{ startedAt: SortOrder.Desc }],
    });
    this.setConversationOptions(
      (result.aiSearchConversationListWithOffset || []).map((x: any) =>
        getAiSearchConversationValues(x, this.langStore)
      )
    );
  }

  getConversationOptions() {
    return this.conversationOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setConversationValue(value: string) {
    this.conversationSearchValue = value;
    yield this.loadConversationValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedConversation(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewAiSearchConversation(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "conversation"
      );
    } else {
      if (value) {
        this.entityValues.showConversation = value;
        this.entityValues.__conversation = value.displayId;
      }
    }
  }

  getQueryValue(): string {
    return this.entityValues.query;
  }

  setQueryValue(value: string) {
    this.entityValues.query = value;
  }

  getResponseValue(): string {
    return this.entityValues.response;
  }

  setResponseValue(value: string) {
    this.entityValues.response = value;
  }

  getSourcesValue(): string {
    return this.entityValues.sources;
  }

  setSourcesValue(value: string) {
    this.entityValues.sources = value;
  }

  getExplanationValue(): string {
    return this.entityValues.explanation;
  }

  setExplanationValue(value: string) {
    this.entityValues.explanation = value;
  }

  getTokenUsageValue(): string {
    return this.entityValues.tokenUsage;
  }

  setTokenUsageValue(value: string) {
    this.entityValues.tokenUsage = value;
  }

  getRatingValue(): EvaluationRating | "" {
    return this.entityValues.rating;
  }

  setRatingValue(value: EvaluationRating | "") {
    this.entityValues.rating = value;
  }

  getRatingOptions() {
    return this.langStore.EvaluationRatingOptions;
  }

  getEvaluatedAtValue(): Date | null {
    return this.entityValues.evaluatedAt;
  }

  setEvaluatedAtValue(value: Date | null) {
    this.entityValues.evaluatedAt = value;
  }

  setEvaluatedByOptions(values: UserValuesType[]) {
    this.evaluatedByOptions = values;
  }

  *loadEvaluatedByValues(): any {
    let where: any = null;
    if (this.evaluatedBySearchValue) {
      const searchFilter = {
        OR: [
          {
            email: {
              contains: this.evaluatedBySearchValue,
              mode: "insensitive",
            },
          },
          {
            name: {
              contains: this.evaluatedBySearchValue,
              mode: "insensitive",
            },
          },
        ],
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.userListWithOffset({
      offset: 0,
      take: 100,
      where: where,
    });
    this.setEvaluatedByOptions(
      (result.userListWithOffset || []).map((x: any) =>
        getUserValues(x, this.langStore)
      )
    );
  }

  getEvaluatedByOptions() {
    return this.evaluatedByOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setEvaluatedByValue(value: string) {
    this.evaluatedBySearchValue = value;
    yield this.loadEvaluatedByValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedEvaluatedBy(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewUser(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "evaluatedBy"
      );
    } else {
      if (value) {
        this.entityValues.showEvaluatedBy = value;
        this.entityValues.__evaluatedBy = value.displayId;
      }
    }
  }

  toolCallsLoadRow(row: any) {
    this.toolCallsCreateStore.loadValuesFromRow(row);
  }

  toolCallsRestore(id: string) {
    const toolCalls = this.entityValues.showToolCalls.filter(
      (x) => x.id === id
    )[0];
    if (toolCalls) {
      toolCalls.__status__ = restoredStatus(toolCalls.__status__);
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  toolCallsValidateChanges(_changes: GridChangeSet) {
    const errors = this.toolCallsCreateStore.validateData(
      this.toolCallsCreateStore.entityValues
    );

    this.toolCallsCreateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  toolCallsCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.toolCallsCreateStore.entityValues;
      const toolCalls = this.entityValues.showToolCalls;
      for (
        let index = 0;
        index < this.entityValues.showToolCalls.length;
        index++
      ) {
        if (toolCalls[index].id === values.id) {
          toolCalls[index] = {
            ...values,
            __status__: updatedStatus(values.__status__),
          };
          break;
        }
      }
      this.toolCallsCreateStore.clearFields();
    } else if (changes.added) {
      const values = this.toolCallsCreateStore.entityValues;
      if (!this.entityValues.showToolCalls) {
        this.entityValues.showToolCalls = [];
      }
      this.entityValues.showToolCalls = [
        ...this.entityValues.showToolCalls,
        {
          ...values,
          id: `${this.uniqueCounter++}`,
          __status__: "added",
        },
      ];
      this.toolCallsCreateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const toolCalls = this.entityValues.showToolCalls;
      for (
        let index = 0;
        index < this.entityValues.showToolCalls.length;
        index++
      ) {
        if (toolCalls[index].id === id) {
          if (isOriginalCrudStatus(toolCalls[index].__status__)) {
            toolCalls[index] = {
              ...toolCalls[index],
              __status__: deleteStatus(toolCalls[index].__status__),
            };
          } else {
            this.entityValues.showToolCalls =
              this.entityValues.showToolCalls.filter((x) => x.id !== id);
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.toolCalls);
    //   this.setCRUDRowsFromValues(newChanges, this.toolCallsCreateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.toolCalls = getNewCRUDRows(newChanges, this.entityValues.toolCalls, this.uniqueCounter++);
    // }
  }

  addNewAiSearchConversation(
    notFound: string | undefined,
    callerFieldName?: string
  ) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "AiSearchResponse",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/aiSearchConversation/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewUser(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "AiSearchResponse",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/user/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.entityValues = AiSearchResponseValuesInit();
    this.cardState = { null: { open: true, errors: [] } };
    this.conversationSearchValue = "";
    this.conversationOptions = [];
    this.evaluatedBySearchValue = "";
    this.evaluatedByOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: AiSearchResponseValuesType): AiSearchResponseErrorsType {
    const errors: AiSearchResponseErrorsType = {};
    this.clearErrors();
    this.conversationError = undefined;
    if (values.showConversation === null || values.showConversation.id === "") {
      errors.conversation = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.AiSearchResponseConversationLabel,
      };
    }
    values.query = values.query.trim();
    if (values.query === "") {
      errors.query = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.AiSearchResponseQueryLabel,
      };
    }
    values.response = values.response.trim();
    if (values.response === "") {
      errors.response = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.AiSearchResponseResponseLabel,
      };
    }
    if (values.showToolCalls.length === 0) {
      errors.toolCalls = {
        message: this.langStore.atLeastOneValue,
        fieldLabel: this.langStore.AiSearchResponseToolCallsLabel,
      };
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      undefined,
      "AiSearchResponseCreateStore"
    );
  }

  *duplicatesAiSearchResponse(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    const aiSearchResponseInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (aiSearchResponseInstance) {
      this.entityValues = getAiSearchResponseValues(
        aiSearchResponseInstance,
        this.langStore,
        true
      );
    }
  }

  close(returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else if (returnData?.resultId && !this.rootStore.router.canGoBack()) {
      // Sin página anterior (una pestaña nueva), volver atrás no navega: el
      // formulario quedaría abierto, y un segundo Guardar daría de alta otro
      // registro. Pasa a la vista del registro creado.
      this.rootStore.router.replace(
        `/aiSearchResponse/view/${returnData.resultId}`
      );
    } else {
      this.rootStore.router.goBack();
    }
  }

  *saveNewAiSearchResponse(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: daría de alta
    // el registro dos veces.
    if (this.isProcessing) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que cierre como si éste hubiera
      // salido bien.
      this.savedId = null;
      // Mientras manda, el formulario está bloqueado por su capa.
      this.isSaving = true;
      try {
        this.status = "processing";
        yield this._save();
        this.status = "ok";
      } catch (error) {
        this.status = "error";

        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      } finally {
        this.isSaving = false;
      }

      if (this.savedId) {
        this.saveOriginalState();
        this.close({
          resultId: this.savedId,
          entity: { id: this.savedId, displayId: this.displayId },
          cancelled: false,
        });
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  cancelNewAiSearchResponse(): void {
    this.cancelled = true;

    // Navigation handled by component

    this.close({ cancelled: true });
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      query: this.entityValues.query,
      response: this.entityValues.response,
      sources: this.entityValues.sources,
      explanation: this.entityValues.explanation,
      tokenUsage: this.entityValues.tokenUsage,
      rating: this.entityValues.rating,
      evaluatedAt: this.entityValues.evaluatedAt,
      conversation: this.entityValues.showConversation?.id || null,
      evaluatedBy: this.entityValues.showEvaluatedBy?.id || null,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  getState(): InitializeParams {
    return {
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getAiSearchResponseValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = AiSearchResponseValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
  }

  *_save(): any {
    const variables = getAiSearchResponseCreateVariables(this.entityValues);
    this.savedId = null;
    this.savedEntity = null;
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.aiSearchResponseCreate({
      ...variables,
      idempotencyKey_: this.idempotencyKeyFor(variables),
    });
    this.savedId = result.aiSearchResponseCreate.id;
    this.saveAttempt = null;
    this.savedEntity = {
      ...this.entityValues,
      id: result.aiSearchResponseCreate.id,
    } as any;
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const duplicateFromId = params?.duplicateFromId;

      // Si se proporciona un ID para duplicar (desde router.params)
      if (duplicateFromId) {
        yield this.duplicatesAiSearchResponse(duplicateFromId);
      }

      yield this.loadConversationValues();
      yield this.loadEvaluatedByValues();

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
